import { IConfig, Keys } from '../../config';
import { debugObjectsAtom, Store } from '../../state';
import {
  ConnectionState,
  Currency,
  CurrencyCross,
  EStreamTradeDTO,
  GetBETTradesResponse,
  getInstrumentId,
  getInstrumentIdForce,
  InstrumentListItem,
  InstrumentSubscriptionDataScope,
  InstrumentSubscriptionRequestItem,
  ListedInstrument,
  ListedInstrumentLike,
  QuoteFeedInterval,
  SSESource,
  SubscribeOptions,
} from '../../types';
import { isArrayEqual, NbLogger, ObjectSet } from '../../utils';
import { BaseSSEConnection } from './baseSSEConnection';
import { EStreamClient, GetSearchInstrumentListParams } from './eStreamClient';

export const getSubscribeOptionId = (item: SubscribeOptions): string =>
  `${item.source}_${item.instrument.ric}.${item.instrument.mic}_${item.source}_${item.dataScope}`;

export class EStreamConnection extends BaseSSEConnection<Array<EStreamTradeDTO> | EStreamTradeDTO> {
  protected source = SSESource.EStream;
  private subscriptionQueue = new ObjectSet<SubscribeOptions>(getSubscribeOptionId);

  constructor(
    offlineMode: boolean,
    logger: NbLogger,
    onConnectionStateChange: (connectionState: ConnectionState, broadcastConnectionState: boolean) => void,
    protected config: IConfig,
    protected onEStreamTradesReceived: (data: Array<EStreamTradeDTO> | EStreamTradeDTO) => void,
    protected eStreamClient: EStreamClient,
    protected store: Store,
  ) {
    super(logger, eStreamClient, offlineMode, onConnectionStateChange, config);
  }

  setToken(token: string) {
    this.client.setToken(token);
  }

  refreshMarketRights() {
    return (this.client as EStreamClient).refreshMarketRights();
  }

  override setAsPrimaryInstance() {
    super.setAsPrimaryInstance();
    this.setSubscriptionQueue();
  }

  /**
   * Subscribes to EStream data based on the options
   * @param options list of options to subscribe to
   * @param instanceId by default this is the current browser instance id, however you can add subscription from another instance
   * @param updateSubscriptionQueue if true, it will update the subscription queue, otherwise it will only add the options to the queue (useful when you want to add multiple subscriptions at once)
   */
  addEStreamSubscriptions(options: Array<SubscribeOptions>, instanceId: number, updateSubscriptionQueue = true) {
    const subOptions: Array<SubscribeOptions> = options.map(o => ({
      ...o,
      source: `${Keys.EStreamSubscriptionSourcePrefix}${instanceId}/${o.source}`,
    }));
    this.subscriptionQueue.overWriteAndClone(subOptions);
    if (updateSubscriptionQueue) {
      return this.setSubscriptionQueue();
    }
    return undefined;
  }

  /**
   * Unsubscribes from EStream data based on the options
   * @param options
   * @param instanceId
   * @param updateSubscriptionQueue if true, it will update the subscription queue, otherwise it will only remove the options from the queue (useful when you want to remove multiple subscriptions at once)
   */
  removeEStreamSubscriptions(options: Array<SubscribeOptions>, instanceId: number, updateSubscriptionQueue = true) {
    this.subscriptionQueue.deleteByCondition(item =>
      options.some(
        option =>
          `${Keys.EStreamSubscriptionSourcePrefix}${instanceId}/${option.source}` === item.source &&
          getInstrumentId(option.instrument) === getInstrumentId(item.instrument),
      ),
    );
    if (updateSubscriptionQueue) {
      return this.setSubscriptionQueue();
    }
    return undefined;
  }

  /**
   * Unsubscribes from EStream data based on the instanceId. This is used when the client is not a primary, and it's closed
   * @param instanceId
   */
  removeEStreamSubscriptionsByInstanceId(instanceId: number) {
    this.subscriptionQueue.deleteByCondition(item =>
      item.source.startsWith(`${Keys.EStreamSubscriptionSourcePrefix}${instanceId}/`),
    );
    return this.setSubscriptionQueue();
  }

  /**
   * Returns eStream instrument data of the given rics
   */
  getInstrumentData(
    instruments: Array<ListedInstrument | ListedInstrumentLike>,
    dataScopes: Array<InstrumentSubscriptionDataScope>,
  ) {
    return (this.client as EStreamClient).findInstruments(instruments, dataScopes);
  }

  /**
   * Quick product list (near realtime data, user rights taken into account) from the eStream /search endpoint
   */
  search(params: GetSearchInstrumentListParams): Promise<Array<InstrumentListItem>> {
    return (this.client as EStreamClient).search(params);
  }

  /**
   * Returns eStream chart data of the given ric
   * @param ric
   * @param interval
   * @param from
   * @param to
   */
  getChartData(ric: string, interval: QuoteFeedInterval, from: Date, to: Date, abortController?: AbortController) {
    return (this.client as EStreamClient).getChartData(ric, interval, from, to, abortController);
  }

  /**
   * @returns All available entries for currently open session (1 minute tick)
   */
  public getCachedIntraDayChart(ric: string, abortController?: AbortController) {
    return (this.client as EStreamClient).getCachedIntraDayChart(ric, abortController);
  }

  /**
   * @returns All available entries max 20 years back (1 day tick)
   */
  public getCachedInterDayChart(ric: string, abortController?: AbortController) {
    return (this.client as EStreamClient).getCachedInterDayChart(ric, abortController);
  }

  /**
   * Returns the current eStream subscriptions
   */
  get subscriptions(): Array<InstrumentSubscriptionRequestItem> {
    return (this.client as EStreamClient).subscriptions;
  }

  hasSubscription(
    instrument: ListedInstrument | ListedInstrumentLike,
    dataScope: Array<InstrumentSubscriptionDataScope>,
  ): boolean {
    return !!this.subscriptions.find(
      _ => getInstrumentIdForce(_) === getInstrumentIdForce(instrument) && isArrayEqual(_.datascopes, dataScope),
    );
  }

  /**
   * Returns true if the instrument has an active subscription that covers all the requested data scopes.
   * Unlike {@link hasSubscription} this also accepts a wider subscription (e.g. the details screen
   * subscribes with MarketData + Book10, which still delivers MarketData).
   */
  hasSubscriptionCovering(
    instrument: ListedInstrument | ListedInstrumentLike,
    dataScope: Array<InstrumentSubscriptionDataScope>,
  ): boolean {
    return !!this.subscriptions.find(
      _ =>
        getInstrumentIdForce(_) === getInstrumentIdForce(instrument) &&
        dataScope.every(scope => _.datascopes.includes(scope)),
    );
  }

  getBETTrades(from: Date, limit?: number, fromMetaLastId?: string): Promise<GetBETTradesResponse> {
    return (this.client as EStreamClient).getBETTrades(from, limit, fromMetaLastId);
  }

  hasToken() {
    return this.client.hasToken();
  }

  async getExchangeRate(
    currencyCrosses: Array<{ base: Currency; quote: Currency }>,
  ): Promise<Map<CurrencyCross, number>> {
    return (this.client as EStreamClient).getExchangeRate(currencyCrosses);
  }

  protected override onMessageReceived(message: Array<EStreamTradeDTO> | EStreamTradeDTO) {
    this.onEStreamTradesReceived(message);
  }

  protected override async sseConnected() {
    try {
      (this.client as EStreamClient).handleSubscriptions();
    } catch (err) {
      this.logger.error(`Failed to resend subscriptions after reconnect`, err);
    }
    return;
  }

  private setSubscriptionQueue() {
    this.addDebugObject(
      'subscriptionQueue',
      this.subscriptionQueue
        .values()
        .map(_ => `${_.instrument.ric}/${_.instrument.mic} (${_.dataScope.join(',')}) / ${_.source}`),
    );

    this.eStreamClient.handleSubscriptions(this.subscriptionQueue);
    return this.subscriptionQueue.copy();
  }

  private addDebugObject<T>(key: string, value: T) {
    this.store.set(debugObjectsAtom, { [key]: value });
  }
}
